import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from "@/components/ui/toaster"
import { AuthProvider } from './contexts/AuthContext';
import MobileErrorBoundary from './components/debug/MobileErrorBoundary';
import VersionIndicator from './components/debug/VersionIndicator';
import { getVersionInfo } from './config/version';
import { initializeCacheManager } from './utils/cacheControl';

import Index from './pages/Index';
import HowItWorks from './pages/HowItWorks';
import InTheMedia from './pages/InTheMedia';
import Contact from './pages/Contact';
import ClaimCompany from './pages/ClaimCompany';
import Pricing from './pages/Pricing';
import MediaMonitoring from './pages/MediaMonitoring';
import FinancialReporting from './pages/FinancialReporting';
import RecruiterReviews from './pages/RecruiterReviews';
import RecruiterLogin from './pages/RecruiterLogin';
import RecruiterSignup from './pages/RecruiterSignup';
import RecruiterDashboard from './pages/RecruiterDashboard';
import Login from './pages/Login';
import Reviews from './pages/Reviews';
import AddReview from './pages/AddReview';
import ThankYou from './pages/ThankYou';
import RequestCompany from './pages/RequestCompany';
import AdminDashboard from './pages/AdminDashboard';
import CompanySearchPage from './pages/CompanySearch';
import CompanyProfile from './pages/CompanyProfile';
import CompanyComparison from './pages/CompanyComparison';
import CompanyComparisonResults from './pages/CompanyComparisonResults';
import AddCompany from './pages/AddCompany';
import EditCompany from './pages/EditCompany';
import NotFound from './pages/NotFound';
import ProtectedRoute from './components/ProtectedRoute';
import Signup from './pages/Signup';
import CompanyLogin from './pages/CompanyLogin';
import CompanySignup from './pages/CompanySignup';
import CompanyDashboard from './pages/CompanyDashboard';
import FlagImageGenerator from './components/images/FlagImageGenerator';

// ScrollToTop component to handle scrolling to top on route changes
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

function App() {
  useEffect(() => {
    // Initialize cache management
    initializeCacheManager();
    
    // Enhanced app initialization logging
    const versionInfo = getVersionInfo();
    console.log('🚀 RedFlags App Initialized:', {
      version: versionInfo.version,
      deploymentId: versionInfo.deploymentId,
      buildTimestamp: new Date(versionInfo.buildTimestamp).toLocaleString(),
      environment: versionInfo.environment,
      cacheKey: versionInfo.cacheKey,
      isMobile: versionInfo.isMobile,
      url: window.location.href,
      domain: window.location.hostname
    });
    
    // Log domain-specific information
    if (window.location.hostname === 'redflags.au') {
      console.log('✅ Running on production domain: redflags.au');
    } else if (window.location.hostname.includes('lovable.app')) {
      console.log('🔧 Running on Lovable staging domain');
    } else {
      console.log('🏠 Running on local development');
    }
    
    // Enhanced cache control headers via meta tags
    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[http-equiv="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.httpEquiv = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };
    
    updateMetaTag('Cache-Control', 'no-cache, no-store, must-revalidate');
    updateMetaTag('Pragma', 'no-cache');
    updateMetaTag('Expires', '0');
    
    // Add deployment ID to document for debugging
    document.documentElement.setAttribute('data-deployment-id', versionInfo.deploymentId);
    document.documentElement.setAttribute('data-build-timestamp', versionInfo.buildTimestamp.toString());
    document.documentElement.setAttribute('data-version', versionInfo.version);
    
    // Force favicon refresh
    const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (favicon) {
      const href = favicon.href;
      favicon.href = '';
      setTimeout(() => {
        favicon.href = href + '?v=' + versionInfo.buildTimestamp;
      }, 100);
    }
  }, []);

  return (
    <MobileErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/in-the-media" element={<InTheMedia />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/claim-company" element={<ClaimCompany />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/companies/:companyName/media-monitoring" element={<MediaMonitoring />} />
            <Route path="/companies/:companyName/financial-reporting" element={<FinancialReporting />} />
            <Route path="/companies/:companyName/recruiter-reviews" element={<RecruiterReviews />} />
            <Route path="/recruiter-login" element={<RecruiterLogin />} />
            <Route path="/recruiter-signup" element={<RecruiterSignup />} />
            <Route path="/recruiter-dashboard" element={
              <ProtectedRoute>
                <RecruiterDashboard />
              </ProtectedRoute>
            } />
            
            {/* Added new route for Flag Image Generator */}
            <Route path="/flag-image" element={<FlagImageGenerator />} />
            
            {/* Company authentication routes */}
            <Route path="/company-login" element={<CompanyLogin />} />
            <Route path="/company-signup" element={<CompanySignup />} />
            <Route 
              path="/company-dashboard" 
              element={
                <ProtectedRoute companyOnly={true}>
                  <CompanyDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Company comparison routes */}
            <Route path="/compare" element={<CompanyComparison />} />
            <Route path="/compare-results" element={<CompanyComparisonResults />} />
            
            {/* Add redirect for old route format */}
            <Route path="/company/:companyId" element={<CompanyProfile />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            
            {/* Public routes that allow guest access */}
            <Route path="/companies" element={<CompanySearchPage />} />
            <Route path="/companies/:companyId" element={<CompanyProfile />} />
            
            {/* Protected add-review routes - require authentication */}
            <Route path="/add-review" element={
              <ProtectedRoute>
                <AddReview />
              </ProtectedRoute>
            } />
            <Route path="/add-review/:companyId" element={
              <ProtectedRoute>
                <AddReview />
              </ProtectedRoute>
            } />
            
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/request-company" element={<RequestCompany />} />
            
            {/* Reviews page - no longer requires authentication */}
            <Route path="/reviews" element={<Reviews />} />
            
            {/* Admin routes */}
            <Route 
              path="/admin/dashboard" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/add-company" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <AddCompany />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/edit-company/:companyId" 
              element={
                <ProtectedRoute requiredRole="admin">
                  <EditCompany />
                </ProtectedRoute>
              } 
            />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster />
          <VersionIndicator />
        </BrowserRouter>
      </AuthProvider>
    </MobileErrorBoundary>
  );
}

export default App;
